import { DocumentDTO } from "../types/DocumentDTO";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Stack,
  IconButton,
  Dialog,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  MenuItem,
} from "@mui/material";
import DocumentPdfPreview from "./document-details/DocumentPdfPreview";
import DocumentActionsBar from "./document-details/DocumentActionsBar";
import DocumentAttachments from "./document-details/DocumentAttachments";
import LabelValueRow from "./common/LabelValueRow";
import { formatCurrencyAmount } from "../utils/formatters";
import DocumentMpkTable from "./document-details/DocumentMpkTable";
import DocumentRetainedAmountsTable from "./document-details/DocumentRetainedAmountsTable";
import ReadonlyTableSection from "./common/ReadonlyTableSection";
import DocumentCombinedHistoryTable from "./document-details/DocumentCombinedHistoryTable";
import VatTable from "./document-details/VatTable";
import { useDocumentVat } from "../hooks/useDocumentVat";
import { useAuth } from "../context/AuthContext";
import { useEffect, useState } from "react";
import { apiFetch } from "../api/apiFetch";
import {
  assignDocumentOwner,
  updateProcessedExternally,
  updatePaymentStatus,
  updateDueDate,
  updateDocumentDescription,
  getPaymentMethodOptions,
  updatePaymentMethod,
} from "../api/documents";
import type { PaymentMethodOption } from "../api/documents";
import { formatDateToDDMMYYYY } from "../utils/dateFormetter";
import { useMpkDimensions } from "../hooks/useMpkDimensions";
import { PAYMENT_STATUS_CONFIG } from "../constants/paymentStatus";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentHistoryDialog from "./document-details/AssignmentHistoryDialog";
import { useAppTranslation } from "../hooks/useAppTranslation";
import ExpandableText from "./common/ExpandableText";
import DocumentSettlementSection from "./document-details/DocumentSettlementSection";
import { getSettlementFeatures } from "../api/settlementLines";

type Props = {
  doc: DocumentDTO;
  companyId: string;
  onBack: () => void;
  onApprove: () => void;
  onReject: () => void;
  onReload: () => void;
};

type DocumentColumnPermissions = {
  canMarkProcessedExternally: boolean;
  canMarkProcessed: boolean;
};

export default function DocumentDetails({ doc, companyId, onBack, onApprove, onReject, onReload }: Props) {
  const { t } = useAppTranslation("common");

  const { user } = useAuth();

  const isGlobalAdmin = user?.role === "admin";
  const isCompanyAdmin =
    user?.companyPolicy?.is_company_admin === true;

  const canAssignOwner = isGlobalAdmin || isCompanyAdmin;

  const [documentColumnPermissions, setDocumentColumnPermissions] =
    useState<DocumentColumnPermissions>({
      canMarkProcessedExternally: false,
      canMarkProcessed: false,
    });

  const canMarkProcessedExternally =
    documentColumnPermissions.canMarkProcessedExternally;

  const [ownerOptions, setOwnerOptions] = useState<any[]>([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>("");
  const [assigningOwner, setAssigningOwner] = useState(false);
  const [updatingPaymentStatus, setUpdatingPaymentStatus] = useState(false);
  const [settlementEnabled, setSettlementEnabled] = useState(false);
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [settlementDirty, setSettlementDirty] = useState(false);
  const [settlementBusy, setSettlementBusy] = useState(false);

  const closeSettlement = () => {
    if (settlementBusy) return;
    setSettlementOpen(false);
    if (settlementDirty) { setSettlementDirty(false); onReload(); }
  };

  useEffect(() => {
    let active = true;
    setSettlementEnabled(false);
    if (companyId) {
      getSettlementFeatures(companyId)
        .then((flags) => { if (active) setSettlementEnabled(flags.useKSeFSettlementHandling === true); })
        .catch(() => { if (active) setSettlementEnabled(false); });
    }
    return () => { active = false; };
  }, [companyId]);

  const {
    rows: vatRows,
    loading: vatLoading,
    error: vatError,
  } = useDocumentVat(companyId, doc.id);

  const [assignedEmail, setAssignedEmail] = useState(
    doc.assignedUserEmail || null
  );

  useEffect(() => {
    setAssignedEmail(doc.assignedUserEmail || null);
  }, [doc.assignedUserEmail]);

  useEffect(() => {
    if (!canAssignOwner || !companyId) return;

    const loadUsers = async () => {
      try {
        const resolveRes = await apiFetch(
          `${process.env.REACT_APP_API_URL}/companies/resolve?bcCompanyId=${companyId}`
        );
        const resolved = await resolveRes.json();

        const usersRes = await apiFetch(
          `${process.env.REACT_APP_API_URL}/companies/${resolved.id}/users`
        );
        const users = await usersRes.json();

        setOwnerOptions(users);
      } catch (err) {
        console.error("Błąd pobierania userów firmy", err);
      }
    };

    loadUsers();
  }, [canAssignOwner, companyId]);

  useEffect(() => {
    let cancelled = false;

    const loadDocumentColumnPermissions = async () => {
      if (!companyId) return;

      try {
        const resolveRes = await apiFetch(
          `${process.env.REACT_APP_API_URL}/companies/resolve?bcCompanyId=${companyId}`
        );

        const resolved = await resolveRes.json();

        const permissionsRes = await apiFetch(
          `${process.env.REACT_APP_API_URL}/companies/${resolved.id}/document-column-permissions`
        );

        const permissions = await permissionsRes.json();

        if (cancelled) return;

        setDocumentColumnPermissions({
          canMarkProcessedExternally:
            permissions.can_mark_processed_externally === true,
          canMarkProcessed:
            permissions.can_mark_processed === true,
        });
      } catch (err) {
        console.error("Błąd pobierania uprawnień kolumn dokumentu", err);

        if (!cancelled) {
          setDocumentColumnPermissions({
            canMarkProcessedExternally: false,
            canMarkProcessed: false,
          });
        }
      }
    };

    loadDocumentColumnPermissions();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const handleOwnerChange = async (userId: string | null) => {
    try {
      setAssigningOwner(true);

      await assignDocumentOwner(doc.id, companyId, userId);

      if (!userId) {
        setAssignedEmail(null);
      } else {
        const selectedUser = ownerOptions.find(
          (u) => u.id === userId
        );
        setAssignedEmail(selectedUser?.email || null);
      }

    } catch (err) {
      console.error("Błąd przypisania właściciela", err);
    } finally {
      setAssigningOwner(false);
    }
  };


  useEffect(() => {
    if (ownerOptions.length === 0) return;

    if (!assignedEmail) {
      setSelectedOwnerId("");
      return;
    }

    const matchedUser = ownerOptions.find(
      (u) => u.email === assignedEmail
    );

    setSelectedOwnerId(matchedUser?.id ?? "");
  }, [assignedEmail, ownerOptions]);

  const ownerDisplayName = (() => {
    if (!assignedEmail) return null;

    const matched = ownerOptions.find(
      (u) => u.email === assignedEmail
    );

    return matched
      ? matched.display_name || matched.email
      : assignedEmail;
  })();

  const mpk = useMpkDimensions(doc, companyId);

  const {
    hasAnyDimension,
    isMPKValid,
    loading: mpkLoading,
  } = mpk;

  const canApproveWithoutMpk =
    isGlobalAdmin ||
    user?.companyPolicy?.can_approve_without_mpk === true;

  const isMpkIncomplete =
    hasAnyDimension &&
    !isMPKValid;

  const isMpkBypassActive =
    !mpkLoading &&
    isMpkIncomplete &&
    canApproveWithoutMpk;

  const canApprove =
    !mpkLoading &&
    (
      !isMpkIncomplete ||
      canApproveWithoutMpk
    );

  const [processedExternally, setProcessedExternally] = useState(
    doc.processedExternally
  );

  const [updatingProcessedExternally, setUpdatingProcessedExternally] = useState(false);

  useEffect(() => {
    setProcessedExternally(doc.processedExternally);
  }, [doc.processedExternally]);

  const [localPaymentStatus, setLocalPaymentStatus] = useState(doc.paymentStatus);

  useEffect(() => {
    setLocalPaymentStatus(doc.paymentStatus);
  }, [doc.paymentStatus]);

  const handlePaymentStatusChange = async (value: string) => {
    try {
      setUpdatingPaymentStatus(true);

      await updatePaymentStatus(doc.id, companyId, value);

      const selected = PAYMENT_STATUS_CONFIG[value];

      setLocalPaymentStatus({
        code: value,
        label: selected?.label ?? value,
      });

    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : t("Nie udało się zaktualizować statusu płatności"));
    } finally {
      setUpdatingPaymentStatus(false);
    }
  };

  const statusCode = doc.status?.code ?? "";
  const isPosted = statusCode === "Posted";

  const [localDueDate, setLocalDueDate] = useState<string | null>(
    doc.dates?.dueDate ?? null
  );

  useEffect(() => {
    const due = doc.dates?.dueDate;

    if (!due || due === "0001-01-01") {
      setLocalDueDate(null);
    } else {
      setLocalDueDate(due);
    }
  }, [doc.dates?.dueDate]);

  const [dueDateDialogOpen, setDueDateDialogOpen] = useState(false);
  const [newDueDate, setNewDueDate] = useState<string>("");
  const [savingDueDate, setSavingDueDate] = useState(false);

  const openDueDateDialog = () => {
    if (isPosted) return;
    
    const isInvalidDate =
      !localDueDate ||
      localDueDate === "0001-01-01";

    setNewDueDate(
      isInvalidDate ? getTodayISO() : localDueDate
    );

    setDueDateDialogOpen(true);
  };

  const saveDueDate = async () => {
    try {
      if (!newDueDate) {
        alert(t("Wybierz nowy termin płatności"));
        return;
      }

      setSavingDueDate(true);

      await updateDueDate(doc.id, companyId, newDueDate);

      setLocalDueDate(newDueDate);

      setDueDateDialogOpen(false);

    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : t("Nie udało się zaktualizować terminu płatności"));
    } finally {
      setSavingDueDate(false);
    }
  };

  const getTodayISO = () => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // YYYY-MM-DD
  };

  const [historyOpen, setHistoryOpen] = useState(false);

  const MAX_DESCRIPTION_LENGTH = 250;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);

  const getCounterColor = (length: number) => {
    if (length > MAX_DESCRIPTION_LENGTH) return "error.main";
    if (length > MAX_DESCRIPTION_LENGTH * 0.9) return "warning.main";
    return "text.secondary";
  };

  const [description, setDescription] = useState(doc.description || "");
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [newDescription, setNewDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);

  useEffect(() => {
    setDescription(doc.description || "");
  }, [doc.description]);

  const openDescriptionDialog = () => {
    setNewDescription(description || "");
    setDescriptionDialogOpen(true);
  };

  const saveDescription = async () => {
    if (newDescription.length > MAX_DESCRIPTION_LENGTH) {
      setDescriptionError(
        t("Opis nie może przekraczać 250 znaków")
      );
      return;
    }

    try {
      setSavingDescription(true);
      setDescriptionError(null);

      await updateDocumentDescription(doc.id, companyId, newDescription);

      setDescription(newDescription);
      setDescriptionDialogOpen(false);

      // ✅ SUCCESS MESSAGE
      setSnackbarOpen(true);

    } catch (e) {
      console.error(e);
      setDescriptionError(e instanceof Error ? e.message : t("Nie udało się zapisać opisu"));
    } finally {
      setSavingDescription(false);
    }
  };

  const isApproved = statusCode === "Approved";
  const isConfirmed = statusCode === "Confirmed";

  const [localPaymentMethod, setLocalPaymentMethod] = useState({
    code: doc.payment?.method ?? "",
    label: doc.payment?.methodDescription ?? doc.payment?.method ?? "",
  });

  useEffect(() => {
    setLocalPaymentMethod({
      code: doc.payment?.method ?? "",
      label: doc.payment?.methodDescription ?? doc.payment?.method ?? "",
    });
  }, [doc.payment?.method, doc.payment?.methodDescription]);

  const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<PaymentMethodOption[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [savingPaymentMethod, setSavingPaymentMethod] = useState(false);

  const paymentMethodLockReason = (() => {
    if (isPosted || isConfirmed) {
      return t("Nie można zmienić formy płatności po zaksięgowaniu dokumentu");
    }

    if (isApproved) {
      return t("Nie można zmienić formy płatności po zaakceptowaniu dokumentu");
    }

    return "";
  })();

  const canChangePaymentMethod =
    canAssignOwner && !paymentMethodLockReason;

  const openPaymentMethodDialog = async () => {
    if (!canChangePaymentMethod) return;

    setSelectedPaymentMethod(localPaymentMethod.code || "");
    setPaymentMethodDialogOpen(true);

    if (paymentMethodOptions.length > 0) return;

    try {
      setLoadingPaymentMethods(true);
      const options = await getPaymentMethodOptions(companyId);
      setPaymentMethodOptions(options);
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : t("Nie udało się pobrać form płatności"));
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const savePaymentMethod = async () => {
    try {
      if (!selectedPaymentMethod) {
        alert(t("Wybierz formę płatności"));
        return;
      }

      setSavingPaymentMethod(true);

      const result = await updatePaymentMethod(
        doc.id,
        companyId,
        selectedPaymentMethod
      );

      const selected = paymentMethodOptions.find(
        (option) => option.value === selectedPaymentMethod
      );

      setLocalPaymentMethod({
        code: result?.paymentMethod ?? selectedPaymentMethod,
        label:
          result?.paymentMethodDescription ??
          selected?.label ??
          selectedPaymentMethod,
      });

      setPaymentMethodDialogOpen(false);
    } catch (e) {
      console.error(e);
      alert(e instanceof Error ? e.message : t("Nie udało się zaktualizować formy płatności"));
    } finally {
      setSavingPaymentMethod(false);
    }
  };

  return (
    <Box sx={{ p: 3, overflowY: "auto", height: "100%" }}>
      {/* ACTIONS */}
      <DocumentActionsBar
        status={doc.status?.label ?? "Unknown"}
        isPosted={isPosted}
        onApprove={onApprove}
        onReject={onReject}
        onBack={onBack}
        canAssignOwner={canAssignOwner}
        ownerOptions={ownerOptions}
        selectedOwnerId={selectedOwnerId}
        onOwnerChange={handleOwnerChange}
        onOpenAssignmentHistory={() => setHistoryOpen(true)}
        ownerDisplayName={ownerDisplayName}
        assigningOwner={assigningOwner}
        canApprove={canApprove}
        isMpkIncomplete={isMpkIncomplete}
        canApproveWithoutMpk={canApproveWithoutMpk}
        isMpkBypassActive={isMpkBypassActive}
        processedExternally={processedExternally}
        canMarkProcessedExternally={canMarkProcessedExternally}
        updatingProcessedExternally={updatingProcessedExternally}
        onProcessedExternallyChange={async (value) => {
          setUpdatingProcessedExternally(true);

          try {
            setProcessedExternally(value);

            await updateProcessedExternally(
              doc.id,
              companyId,
              value
            );
          } catch (e) {
            console.error(e);
            setProcessedExternally(prev => !prev);
            alert(e instanceof Error ? e.message : t("Nie udało się zaktualizować pola"));
          } finally {
            setUpdatingProcessedExternally(false);
          }
        }}
        paymentStatus={localPaymentStatus?.code}
        paymentStatusText={localPaymentStatus?.label}
        onPaymentStatusChange={handlePaymentStatusChange}
        updatingPaymentStatus={updatingPaymentStatus}
      />

      {/* HEADER */}
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("Faktura nr.")} {doc.externalNumber || doc.number}
      </Typography>

      {/* ================= CONTENT + PDF ================= */}
        <Box
          sx={{
            display: "flex",
            gap: 3,
            alignItems: "flex-start",
            flexDirection: {
              xs: "column",
              md: "column",
              lg: "row",
            },
          }}
        >
        {/* ===== LEFT COLUMN (50%) ===== */}
        <Box
          sx={{
            flex: 1,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Stack spacing={2}>
            {/* INFORMACJE PODSTAWOWE */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t("Podstawowe informacje")}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <LabelValueRow
                label="document_status"
                value={t(doc.status?.label, {}, "statuses")}
              />
              <LabelValueRow
                label="document_type"
                value={t(doc.purchaseCost, {}, "documents")}
              />
              <LabelValueRow
                label="document_category"
                value={t(doc.category?.name, {}, 'documents')}
              />
              <LabelValueRow label="document_ksef_no" value={doc.ksefNo ?? t("Brak numeru KSeF")} />

              <Divider sx={{ my: 2 }} />

              <LabelValueRow
                label="document_date"
                value={formatDateToDDMMYYYY(doc.dates.documentDate)}
              />
              <LabelValueRow
                label="created_at"
                value={formatDateToDDMMYYYY(doc.dates.invoiceCreationDate)}
              />
              <LabelValueRow label="sale_date" value={formatDateToDDMMYYYY(doc.dates.serviceGoodDeliveryDate)} />
              <LabelValueRow
                label="payment_date"
                value={formatDateToDDMMYYYY(localDueDate)}
                actions={
                  canAssignOwner ? (
                    <Tooltip title={isPosted ? "Nie można zmienić terminu płatności po zaksięgowaniu dokumentu" : "Zmień termin płatności"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={openDueDateDialog}
                          aria-label="Zmień termin płatności"
                          disabled={isPosted}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null
                }
              />

              <Divider sx={{ my: 2 }} />

              <LabelValueRow
                label="paymentMethod"
                value={localPaymentMethod.label || t("Brak formy płatności")}
                actions={
                  canAssignOwner ? (
                    <Tooltip title={paymentMethodLockReason || t("Zmień formę płatności")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={openPaymentMethodDialog}
                          aria-label={t("Zmień formę płatności")}
                          disabled={!canChangePaymentMethod}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null
                }
              />

              <Divider sx={{ my: 2 }} />

              <LabelValueRow
                label="document_description_label"
                value={<ExpandableText text={description} maxLines={1} />}
                actions={
                  canAssignOwner ? (
                    <Tooltip title={t("edit_document_description")}>
                      <IconButton size="small" onClick={openDescriptionDialog}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null
                }
              />
            </Paper>

            {/* KONTRAHENT */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t("Kontrahent")}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <LabelValueRow label="vendor_name" value={doc.vendor.name} />
              <LabelValueRow label="vendor_vat_reg" value={doc.vendor.nip} />
              <LabelValueRow label="vendor_address" value={doc.vendor.address} />
            </Paper>

            {/* KWOTY */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t("Kwoty")}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <LabelValueRow
                label="net_ammount"
                value={`${formatCurrencyAmount(doc.amounts.net)} ${doc.amounts.currency}`}
                valueAlign="right"
              />
              <LabelValueRow
                label="vat_amount"
                value={`${formatCurrencyAmount(doc.amounts.vat)} ${doc.amounts.currency}`}
                valueAlign="right"
              />
              <LabelValueRow
                label="gross_amount"
                value={`${formatCurrencyAmount(doc.amounts.gross)} ${doc.amounts.currency}`}
                valueAlign="right"
              />
            </Paper>

            {/* VAT */}
            <ReadonlyTableSection
              title="Stawki VAT"
              actions={settlementEnabled ? (
                <Button size="small" variant="outlined" onClick={() => setSettlementOpen(true)}>
                  Obciążenia i odliczenia
                </Button>
              ) : undefined}
              loading={vatLoading}
              error={vatError}
              empty={vatRows.length === 0}
              emptyText="Brak zdefiniowanych stawek VAT dla tego dokumentu."
            >
              <VatTable rows={vatRows} />
            </ReadonlyTableSection>

            <Dialog open={settlementOpen} fullWidth maxWidth="md" disableEscapeKeyDown
              onClose={(_, reason) => { if (reason !== "backdropClick") closeSettlement(); }}
              aria-label="Obciążenia i odliczenia KSeF">
              <DialogContent sx={{ p: 0, "& > .MuiPaper-root": { mt: 0, boxShadow: "none", borderRadius: 0 } }}>
                {settlementOpen && (
                  <DocumentSettlementSection
                    documentId={doc.id} companyId={companyId} currency={doc.amounts.currency}
                    canEditDocument={doc.status?.code !== "Approved" && doc.status?.code !== "Posted"}
                    onUpdated={() => setSettlementDirty(true)}
                    onBusyChange={setSettlementBusy}
                    onCancel={closeSettlement}
                    onSaved={() => { setSettlementOpen(false); setSettlementDirty(false); onReload(); }}
                  />
                )}
              </DialogContent>
            </Dialog>

            {/* RETAINED / ATTACHMENTS */}
            <DocumentRetainedAmountsTable
              document={doc}
              companyId={companyId}
            />
            <DocumentAttachments
              attachments={doc.attachments}
              companyId={companyId}
              documentId={doc.id}
              documentStatus={doc.status?.code ?? null}
              onReload={onReload}
            />
          </Stack>
        </Box>

        {/* ===== RIGHT COLUMN (50%) – STICKY PDF ===== */}
        <Box
          sx={{
            width: {
              xs: "100%",
              md: "100%",
              lg: "45%",
            },
            position: {
              xs: "static",
              md: "static",
              lg: "sticky",
            },
            top: { lg: 0 },
          }}
        >
          <DocumentPdfPreview
            documentNumber={doc.externalNumber || doc.number || ""}
            documentId={doc.id}
            companyId={companyId}
            ksefNo={doc.ksefNo}
          />
        </Box>
      </Box>

      {/* ================= MPK – FULL WIDTH ================= */}
      <Box sx={{ mt: 2 }}>
        {isMpkBypassActive && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t(
              "Tabela wymiarów nie jest kompletna, ale posiadasz uprawnienie do akceptacji dokumentu bez uzupełnionych wymiarów."
            )}
          </Alert>
        )}

        <DocumentMpkTable
          document={doc}
          companyId={companyId}
          mpk={mpk}
        />
      </Box>

      {/* ================= LOGI – FULL WIDTH ================= */}
      <Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: {
              xs: "column",
              md: "row",
            },
          }}
        >
          <DocumentCombinedHistoryTable
            document={doc}
            companyId={companyId}
          />
        </Box>
      </Box>
      <Dialog open={dueDateDialogOpen} onClose={() => !savingDueDate && setDueDateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("Zmień termin płatności")}</DialogTitle>

        <DialogContent>
          <TextField
            margin="dense"
            label={t("Nowy termin płatności")}
            type="date"
            fullWidth
            value={newDueDate}
            onChange={(e) => setNewDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={savingDueDate}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDueDateDialogOpen(false)}
            disabled={savingDueDate}
          >
            {t("Anuluj")}
          </Button>

          <Button
            onClick={saveDueDate}
            variant="contained"
            disabled={savingDueDate}
          >
            {savingDueDate ? <CircularProgress size={18} /> : t("Zapisz")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={paymentMethodDialogOpen}
        onClose={() => !savingPaymentMethod && setPaymentMethodDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("Zmień formę płatności")}</DialogTitle>

        <DialogContent>
          <TextField
            select
            margin="dense"
            label={t("Nowa forma płatności")}
            fullWidth
            value={selectedPaymentMethod}
            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
            disabled={loadingPaymentMethods || savingPaymentMethod}
          >
            {paymentMethodOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setPaymentMethodDialogOpen(false)}
            disabled={savingPaymentMethod}
          >
            {t("Anuluj")}
          </Button>

          <Button
            onClick={savePaymentMethod}
            variant="contained"
            disabled={loadingPaymentMethods || savingPaymentMethod}
          >
            {savingPaymentMethod ? <CircularProgress size={18} /> : t("Zapisz")}
          </Button>
        </DialogActions>
      </Dialog>
      <AssignmentHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        companyId={companyId}
        documentId={doc.id}
        currentOwnerEmail={assignedEmail}
      />
      <Dialog
        open={descriptionDialogOpen}
        onClose={() => !savingDescription && setDescriptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("Edytuj opis dokumentu")}</DialogTitle>

        <DialogContent>
          <Box sx={{ position: "relative" }}>
            <TextField
              margin="dense"
              label={t("Opis")}
              multiline
              rows={4}
              fullWidth
              value={newDescription}
              onChange={(e) => {
                const value = e.target.value;
                setNewDescription(value);

                if (value.length <= MAX_DESCRIPTION_LENGTH) {
                  setDescriptionError(null);
                }
              }}
              disabled={savingDescription}
              error={!!descriptionError}
              helperText={descriptionError || " "}
            />

            {/* 🔢 LICZNIK W POLU */}
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 16,
                right: 12,
                backgroundColor: "background.paper",
                px: 0.5,
                color: getCounterColor(newDescription.length),
                fontWeight: newDescription.length > MAX_DESCRIPTION_LENGTH * 0.9? 700 : 500,
                transition: "color 0.2s ease, font-weight 0.2s ease",
              }}
            >
              {newDescription.length}/{MAX_DESCRIPTION_LENGTH}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDescriptionDialogOpen(false)}
            disabled={savingDescription}
          >
            {t("Anuluj")}
          </Button>

          <Button
            onClick={saveDescription}
            variant="contained"
            disabled={
              savingDescription ||
              newDescription.length > MAX_DESCRIPTION_LENGTH
            }
          >
            {savingDescription ? <CircularProgress size={18} /> : t("Zapisz")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
        >
          {t("description_saved")}
        </Alert>
      </Snackbar>
    </Box>
  );
}
